/**
 * 포비즈 도메인 규칙의 단일 출처.
 *
 * 상태 전이표와 권한 매트릭스는 JSON에 있고, 이 모듈은 거기에 타입을 씌운다.
 * 백엔드의 상태 머신과 어드민의 버튼 표시가 같은 파일을 읽어야
 * 문서·코드·화면이 어긋나지 않는다.
 *
 * 근거: 포비즈 설계 명세서 3장(전이), 4장(권한)
 */
import transitionsJson from '../order_transitions.json'
import permissionsJson from '../permissions.json'

// ── 역할과 상태 ────────────────────────────────────────────────

export const ROLES = ['COMPANY_ADMIN', 'COMPANY_MEMBER', 'OPERATOR', 'VENDOR_MEMBER'] as const
export type Role = (typeof ROLES)[number]

/**
 * 계정 주체 유형 (BIZ-67). company를 "바이어 계정"으로 재사용하며 이 유형으로 구분한다.
 * COMPANY 기업 / ORG 단체 / INDIVIDUAL 개인 / GROUP 사모임 / ETC 기타.
 */
export const SUBJECT_TYPES = ['COMPANY', 'ORG', 'INDIVIDUAL', 'GROUP', 'ETC'] as const
export type SubjectType = (typeof SUBJECT_TYPES)[number]

export const ORDER_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'INFO_REQUIRED',
  'COST_REVIEW',
  'VENDOR_ASSIGNED',
  'PO_SENT',
  'VENDOR_ACCEPTED',
  'VENDOR_REJECTED',
  'PREPARING',
  'DELIVERING',
  'DELIVERED',
  'DELIVERY_ISSUE',
  'CANCELLED',
] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]

export const TERMINAL_STATES: readonly OrderStatus[] = ['CANCELLED']
export const INITIAL_STATE: OrderStatus = 'DRAFT'

/**
 * 운영자가 주문 본문(수취인·문구·상품·수량·발송 정보 등)을 편집할 수 있는 상태 (BIZ-37, A안).
 * 발주(업체 배정, VENDOR_ASSIGNED) **이전** 구간만 — 이후엔 이미 발주서 받은 업체에
 * 변경을 재통지·재정산해야 해서 복잡도가 급증하므로 편집을 막고 별도 이슈로 둔다.
 * 편집 주체 게이팅 근거: permissions.json `order.reviewAndEdit` (OPERATOR = ALLOW, 기업 = SCOPED).
 * 기업(본인 소속) 셀프편집도 **이 발주 전 구간까지만** 허용한다 — 발주 후 편집은 업체 재통지가
 * 걸려 운영자 전용(ORDER_BODY_EDITABLE_POST_PO_STATES). 취소·발주 후 변경은 여전히 전화.
 */
export const ORDER_BODY_EDITABLE_STATES: readonly OrderStatus[] = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'INFO_REQUIRED',
]

/**
 * 발주(업체 배정) **이후**, 배송 시작 전까지 운영자가 본문을 편집할 수 있는 상태 (BIZ-50).
 * 발주 전(ORDER_BODY_EDITABLE_STATES)과 달리 이 구간은:
 *  - **텍스트 본문만** 편집(문구·수취인·빈소·배송정보). 상품·수량·유형 변경은 금지 —
 *    이미 굳은 공급 스냅샷·정산을 건드려야 하므로.
 *  - 편집 시 **업체 재통지**가 필요하다(이미 발주서를 받았으므로). 통지 이력을
 *    order_change_log.notified_vendor_* 에 남긴다(기획서 8.5).
 * DELIVERING(배송 시작) 이후는 실물 배송 중이라 본문 변경 무의미 → 계속 차단.
 */
export const ORDER_BODY_EDITABLE_POST_PO_STATES: readonly OrderStatus[] = [
  'VENDOR_ASSIGNED',
  'PO_SENT',
  'VENDOR_ACCEPTED',
  'PREPARING',
]

/**
 * 기업 셀프 편집의 **발주 후 유예 시간(분)** (BIZ-71). 자동발주가 접수 즉시 발주까지
 * 밀어버려 편집 창이 0이 되는 문제를, "발주를 미루는" 대신 "발주 후 N분까지 편집 허용"으로 푼다.
 * 스케줄러 없이 읽는 시점의 시간 비교만으로 판정한다(발주=VENDOR_ASSIGNED 진입 시각 기준).
 * 이 창에서도 텍스트만 편집 가능(상품·수량은 공급 스냅샷이 굳어 계속 잠금). 운영자는 이 제한을 안 받는다.
 */
export const ORDER_EDIT_GRACE_MINUTES = 30

// ── 전이 ───────────────────────────────────────────────────────

export interface Transition {
  readonly from: OrderStatus
  readonly to: OrderStatus
  readonly actors: readonly Role[]
  /** 사유 입력이 필수인 전이 */
  readonly requiresReason?: boolean
  /** 취소 비용을 입력할 수 있는 전이. 발주(PO_SENT) 이후에만 참이다. */
  readonly allowsCancellationFee?: boolean
  /** 전이 시 발생하는 사이드이펙트 (알림 발송, 스냅샷 확정 등) */
  readonly effects?: readonly string[]
  /** 전이 전에 채워져 있어야 하는 것 */
  readonly requires?: readonly string[]
  /** 이 조건이 참이면 전이가 차단된다 */
  readonly blockedWhen?: string
  /** 사진 필수 조건을 우회할 수 있는 역할 */
  readonly photoExceptionBypassRoles?: readonly Role[]
}

export const TRANSITIONS = transitionsJson.allowed as unknown as readonly Transition[]

/** 내부 상태 → 기업 노출 상태. 저장하지 않고 파생 계산한다 (명세서 3.2). */
export const COMPANY_FACING_STATUS = transitionsJson.companyFacingStatus as Record<
  OrderStatus,
  string
>

// ── 권한 ───────────────────────────────────────────────────────

/** ALLOW=전면 허용, SCOPED=본인 소속 범위만, DENY=불가 */
export type Permission = 'ALLOW' | 'SCOPED' | 'DENY'
export type PermissionRow = Readonly<Record<Role, Permission>>

/** JSON의 `$`로 시작하는 키는 주석이므로 제외한다. */
function stripComments<T>(obj: Record<string, unknown>): Record<string, T> {
  const out: Record<string, T> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (!key.startsWith('$')) out[key] = value as T
  }
  return out
}

export const ACTION_PERMISSIONS = stripComments<PermissionRow>(permissionsJson.actions)
export const FIELD_PERMISSIONS = stripComments<PermissionRow>(permissionsJson.fields)

/**
 * 업체 발주서에 노출되는 필드 화이트리스트 (기획서 10.3).
 * 블랙리스트로 관리하면 컬럼을 추가할 때마다 유출 위험이 생긴다.
 */
export const VENDOR_PO_FIELDS: readonly string[] = permissionsJson.vendorPurchaseOrderFields
